import { ErrorHandler, Injectable, inject } from '@angular/core';
import { Meter, Tracer, metrics, propagation, trace } from '@opentelemetry/api';
import { W3CTraceContextPropagator } from '@opentelemetry/core';
import {
  OTLPMetricExporter
} from '@opentelemetry/exporter-metrics-otlp-http';
import {
  OTLPTraceExporter
} from '@opentelemetry/exporter-trace-otlp-http';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { Resource, resourceFromAttributes } from '@opentelemetry/resources';
import {
  MeterProvider,
  PeriodicExportingMetricReader
} from '@opentelemetry/sdk-metrics';
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import {
  ATTR_DEPLOYMENT_ENVIRONMENT_NAME,
  ATTR_SERVICE_INSTANCE_ID,
  ATTR_SERVICE_NAME,
  ATTR_SERVICE_NAMESPACE,
  ATTR_SERVICE_VERSION
} from '@opentelemetry/semantic-conventions';

/**
 * Represents the subset of window.WebConfig properties used for telemetry.
 * The runtime config object is injected via environment.js in index.html.
 */
interface WebConfig {
  /** OTLP HTTP collector base URL (e.g. https://123456.collect.observeinc.com/v2/otel). */
  observeCollectorUrl?: string;
  /** Observe Inc bearer token scoped to ingest-only permissions. */
  observeIngestToken?: string;
  /**
   * Explicit deployment environment name reported to the Observe Inc backend
   * (e.g. 'local', 'hybrid', 'dev', 'qa', 'uat', 'production').
   *
   * When present this value is used directly as the `deployment.environment.name`
   * OTel resource attribute, providing per-environment granularity in Observe
   * datasets.  When absent the service falls back to the binary
   * `production ? 'production' : 'non-production'` derivation so that existing
   * environment files without this field continue to work without modification.
   */
  environmentName?: string;
  /** Whether the application is running in production mode. */
  production?: boolean;
  /** Master on/off switch for telemetry. Flip in environment.js on S3 without redeploying. */
  telemetryEnabled?: boolean;
}


/**
 * Service name reported to Observe Inc / OpenTelemetry backends.
 * All MFEs share this name so traces are correlated under one service.
 */
const SERVICE_NAME = 'acadvantage-ui';

/**
 * Logical namespace grouping all ACAdvantage services (UI, APIs, workers).
 * Using a shared namespace enables topology views and service maps in Observe
 * to automatically draw relationships between `acadvantage-ui`,
 * `acadvantage-rehash-api`, `acadvantage-shared-api`, and so on without
 * requiring additional configuration.
 */
const SERVICE_NAMESPACE = 'acadvantage';

/** Semantic version of the shell application. */
const SERVICE_VERSION = '1.0.0';

/**
 * `sessionStorage` key used to persist the per-tab instance identifier across
 * soft navigations within a single browser session.
 *
 * `sessionStorage` scope matches OTel's definition of a "service instance" for
 * a browser app: one tab === one instance.  The value is regenerated when the
 * tab is closed and reopened, producing a fresh instance ID for each new
 * session, which is the desired behaviour.
 */
const INSTANCE_ID_SESSION_KEY = '__acadv_otel_instance_id__';

/**
 * Metric export interval in milliseconds.
 * 60 seconds balances cardinality cost against observability fidelity.
 */
const METRIC_EXPORT_INTERVAL_MS = 60_000;

/**
 * `globalThis` key used as a cross-instance idempotency sentinel.
 *
 * When `@acadv/shared/telemetry` is loaded as a singleton via Module Federation
 * there is one `TelemetryService` instance and the sentinel is never needed.
 * When singleton sharing is not yet active (e.g. a remote MFE loads before the
 * shared scope is fully populated, or a test constructs a second injector), the
 * sentinel prevents a second instance from registering duplicate exporters and
 * overwriting the global OTel tracer provider.
 */
const TELEMETRY_INIT_SENTINEL = '__acadv_otel_initialised__';

/**
 * TelemetryService
 *
 * Initialises OpenTelemetry trace and metric providers once at application
 * startup and registers them as the global providers via the OTel API.
 *
 * Instrumentation points (HTTP auto-instrumentation, user-action spans, etc.)
 * should import this service and call {@link getTracer} / {@link getMeter}
 * to obtain a named tracer or meter.
 *
 * If `window.WebConfig.observeCollectorUrl` is absent (local / CI environments
 * without an Observe Inc endpoint configured), initialisation is skipped
 * entirely so that no network errors are produced in development.
 *
 * @example
 * ```ts
 * constructor(private telemetry: TelemetryService) {
 *   const tracer = this.telemetry.getTracer('rehash-feature');
 *   const span  = tracer.startSpan('submit-rehash-form');
 *   // ... perform work ...
 *   span.end();
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class TelemetryService {
  /** Global WebTracerProvider instance, null when telemetry is disabled. */
  private tracerProvider: WebTracerProvider | null = null;

  /** Global MeterProvider instance, null when telemetry is disabled. */
  private meterProvider: MeterProvider | null = null;

  /**
   * Angular's global error handler, used to emit a safe diagnostic when a
   * provider fails to initialise. Routing through ErrorHandler (rather than
   * raw console) ensures the error is visible in production without printing
   * sensitive values (token, headers) and allows hosts to override the handler.
   */
  private readonly errorHandler = inject(ErrorHandler);

  constructor() {
    this.initialise();
  }

  /**
   * Initialises the OpenTelemetry trace and metric providers.
   *
   * The method is **idempotent** and **retryable**:
   *
   * - Returns immediately when *both* providers are already set on this
   *   instance (re-call guard uses AND, not OR, so a partial-failure state
   *   does not freeze the instance permanently).
   * - Returns immediately when the cross-instance
   *   {@link TELEMETRY_INIT_SENTINEL} is present on `globalThis`
   *   (duplicate-MFE guard).
   * - Each provider is initialised in its own try/catch: a failure in one
   *   step rolls back the field to `null`, leaving the instance in a
   *   retryable state so that a later call can complete the missing piece.
   * - The sentinel is written only after *both* providers are successfully
   *   registered; a partial failure leaves it unset so that a retry is
   *   possible.
   *
   * Reads `window.WebConfig` at call time so that the runtime config injected
   * by environment.js is available. The method returns immediately (no-op)
   * unless all three conditions are met:
   *  1. `telemetryEnabled` is `true`
   *  2. `observeCollectorUrl` is a non-empty string
   *  3. `observeIngestToken` is a non-empty string
   *
   * Requiring a non-empty token prevents unauthenticated exporter
   * initialisation when the deploy-time injection step is skipped or the
   * GitHub environment secret is not yet configured.
   */
  initialise(): void {
    // Instance-level guard: returns only when *both* providers are already set.
    // Using AND (not OR) ensures a partial-failure state (e.g. tracerProvider
    // set but meterProvider threw) is not permanently frozen — a subsequent
    // call can still complete the missing piece.
    if (this.tracerProvider && this.meterProvider) {
      return;
    }

    // Cross-instance guard: a different TelemetryService copy already completed
    // initialisation (e.g. a second MFE that loaded before the shared scope was
    // fully populated).  Prevents duplicate exporters and provider overwrites.
    if ((globalThis as Record<string, unknown>)[TELEMETRY_INIT_SENTINEL]) {
      return;
    }

    const config = this.getWebConfig();

    if (!config?.telemetryEnabled || !config?.observeCollectorUrl || !config?.observeIngestToken) {
      // Telemetry is disabled, the collector URL is absent, or the ingest token
      // was not injected at deploy time. Skip exporter initialisation entirely
      // to avoid repeated unauthenticated HTTP requests to the Observe endpoint.
      return;
    }

    const { observeCollectorUrl, observeIngestToken, production, environmentName } = config;
    // Prefer the explicit environmentName field so each environment tier
    // (local, dev, qa, uat, production) appears as a distinct value in Observe
    // datasets.  Fall back to the legacy boolean-derived label for any
    // environment file that does not yet carry the explicit field.
    const environment = environmentName ?? (production ? 'production' : 'non-production');

    const resource = this.buildResource(environment);
    const headers = this.buildAuthHeaders(observeIngestToken);

    // Initialise each provider independently so that a failure in one step does
    // not permanently block the other.  Each phase is guarded so that a
    // successful prior call does not overwrite an already-registered provider.
    // The try/catch rolls back any partial field assignment on failure, keeping
    // the instance in a state where a later retry can complete initialisation.
    if (!this.tracerProvider) {
      try {
        this.initTraceProvider(observeCollectorUrl, resource, headers);
      } catch (err) {
        // Roll back any partial assignment so the next call can retry.
        this.tracerProvider = null;
        this._reportInitFailure('trace provider', err);
      }
    }

    if (!this.meterProvider) {
      try {
        this.initMeterProvider(observeCollectorUrl, resource, headers);
      } catch (err) {
        // Roll back any partial assignment so the next call can retry.
        this.meterProvider = null;
        this._reportInitFailure('metric provider', err);
      }
    }

    // Write the cross-instance sentinel only when both providers are fully
    // registered. If either step failed its field remains null, preventing the
    // sentinel from suppressing a future retry that could complete initialisation.
    if (this.tracerProvider && this.meterProvider) {
      (globalThis as Record<string, unknown>)[TELEMETRY_INIT_SENTINEL] = true;
    }
  }

  /**
   * Returns a named {@link Tracer} from the global OTel trace API.
   *
   * If the provider was not initialised (no collector URL), the OTel API
   * returns a no-op tracer that silently discards all spans, so callers
   * do not need to guard against null.
   *
   * @param name - Instrumentation scope name, typically the feature or library name.
   * @returns A {@link Tracer} instance bound to the given instrumentation scope.
   */
  getTracer(name: string): Tracer {
    return trace.getTracer(name);
  }

  /**
   * Returns a named {@link Meter} from the global OTel metrics API.
   *
   * If the provider was not initialised (no collector URL), the OTel API
   * returns a no-op meter that silently discards all measurements, so callers
   * do not need to guard against null.
   *
   * Delegating to the global API (rather than the instance-level
   * {@link meterProvider}) ensures that a second {@link TelemetryService}
   * instance — which has `meterProvider === null` because the
   * {@link TELEMETRY_INIT_SENTINEL} short-circuited its initialisation —
   * still returns a functional meter wired to the first instance's provider
   * instead of a silent no-op.
   *
   * @param name - Instrumentation scope name, typically the feature or library name.
   * @returns A {@link Meter} instance bound to the given instrumentation scope.
   */
  getMeter(name: string): Meter {
    return metrics.getMeter(name);
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /**
   * Returns a printable representation of a caught value without throwing.
   *
   * Both `err.message` and `String(err)` can theoretically throw — the former
   * if `message` is defined with a custom getter that throws, the latter if
   * `toString()` or `Symbol.toPrimitive` throws.  The outer `try/catch` ensures
   * `safeErrorMessage` never propagates an exception to its caller.
   *
   * @param err - The value caught from a `catch` clause.
   * @returns The `.message` property for `Error` instances, `String(err)` otherwise,
   *          or `'<unprintable error>'` if both accessors throw.
   */
  private safeErrorMessage(err: unknown): string {
    try {
      return err instanceof Error ? err.message : String(err);
    } catch {
      return '<unprintable error>';
    }
  }

  /**
   * Builds and reports an initialisation-failure diagnostic to the injected
   * `ErrorHandler` without allowing the handler to abort the calling catch block.
   *
   * Some `ErrorHandler` implementations (e.g. the shell app's
   * `ChunkLoadErrorHandler`) re-throw errors that are not chunk load failures.
   * If that re-throw were allowed to escape the provider `catch` block in
   * `initialise()`, telemetry would become fatal and non-retryable.  This helper
   * absorbs any secondary throw from `handleError` and falls back to
   * `console.error` so the diagnostic is never silently lost.
   *
   * The outer `try/catch` guards against the unlikely but possible case where
   * diagnostic construction itself throws (e.g. a custom `message` getter on
   * `err` throws before `safeErrorMessage` can catch it, or `new Error()` itself
   * fails in a severely degraded runtime environment).
   *
   * `cause` is assigned as a plain property rather than via the `Error`
   * constructor second argument because that overload requires the ES2022 lib
   * types not available in this project's TypeScript target.
   *
   * @param context Human-readable label identifying which provider failed.
   * @param err     The value thrown by the provider initialisation code.
   */
  private _reportInitFailure(context: string, err: unknown): void {
    try {
      const diagnostic = new Error(`TelemetryService: ${context} initialisation failed — ${this.safeErrorMessage(err)}`);
      if (err instanceof Error) { (diagnostic as Error & { cause: Error }).cause = err; }
      try {
        this.errorHandler.handleError(diagnostic);
      } catch {
        // ErrorHandler re-threw (e.g. ChunkLoadErrorHandler discards non-chunk
        // errors by re-throwing). Fall back to console so the diagnostic is not lost.
        console.error(diagnostic);
      }
    } catch {
      // Diagnostic construction threw despite safeErrorMessage's own guard
      // (e.g. new Error() failed). Emit a static fallback so the failure is
      // never silently swallowed.
      console.error('TelemetryService: %s initialisation failed — diagnostic construction threw', context);
    }
  }

  /**
   * Safely reads `window.WebConfig` and returns it, or `undefined` when not
   * available (e.g. during unit tests that do not set up the global).
   */
  private getWebConfig(): WebConfig | undefined {
    // environments (e.g. SSR, Node.js test runners) where `window` is not
    // declared. `globalThis.window` is simply `undefined` in those contexts.
    const win = globalThis.window as (Window & typeof globalThis & { WebConfig?: WebConfig }) | undefined;
    return win?.WebConfig;
  }

  /**
   * Constructs an OTel resource describing this service deployment.
   * Uses `resourceFromAttributes` — the 2.x replacement for `new Resource(...)`.
   *
   * Attributes set:
   * - `service.name` — identifies the service in Observe dashboards.
   * - `service.namespace` — groups all ACAdvantage services together in
   *   topology views and protects against name collisions with same-named
   *   services from other teams.
   * - `service.version` — correlates traces/metrics to a specific build.
   * - `deployment.environment.name` — distinguishes environment tiers
   *   (local, dev, qa, uat, production) within a single dataset.
   * - `service.instance.id` — uniquely identifies the browser tab/session
   *   so per-session behavior (e.g. repeated slow spans) can be isolated
   *   from aggregate data.
   *
   * @param environment - Human-readable deployment environment label.
   * @returns A populated {@link Resource} instance.
   */
  private buildResource(environment: string): Resource {
    return resourceFromAttributes({
      [ATTR_SERVICE_NAME]: SERVICE_NAME,
      [ATTR_SERVICE_NAMESPACE]: SERVICE_NAMESPACE,
      [ATTR_SERVICE_VERSION]: SERVICE_VERSION,
      [ATTR_DEPLOYMENT_ENVIRONMENT_NAME]: environment,
      [ATTR_SERVICE_INSTANCE_ID]: this.getOrCreateInstanceId()
    });
  }

  /**
   * Returns a stable identifier for the current browser tab/session, creating
   * and persisting one in `sessionStorage` on the first call.
   *
   * `sessionStorage` scope aligns with OTel's intent for `service.instance.id`
   * in a browser context: each tab is a distinct "running instance".  The ID
   * survives soft navigations and route changes within the tab but is
   * discarded when the tab is closed, so a fresh session always gets a new ID.
   *
   * Falls back to a transient `crypto.randomUUID()` (not persisted) when
   * `sessionStorage` is unavailable — e.g. in non-browser environments (SSR,
   * Jest with a Node runner), or under strict private-browsing policies that
   * block storage access.  The transient ID is still valid for the lifetime of
   * the page load and prevents the attribute from being omitted entirely.
   *
   * @returns A UUID string scoped to the current browser session/tab.
   */
  private getOrCreateInstanceId(): string {
    try {
      const existing = sessionStorage.getItem(INSTANCE_ID_SESSION_KEY);
      if (existing) {
        return existing;
      }
      const id = crypto.randomUUID();
      sessionStorage.setItem(INSTANCE_ID_SESSION_KEY, id);
      return id;
    } catch {
      // sessionStorage is unavailable (non-browser runtime, restrictive
      // private-browsing policy, or security header).  Return a transient UUID
      // that is valid for this page load but will not survive a refresh.
      return crypto.randomUUID();
    }
  }

  /**
   * Builds HTTP request headers for Observe Inc bearer-token authentication.
   *
   * Returns an empty object when no token is provided as a defensive fallback,
   * but `initialise()` now guards against calling this method with an absent
   * token, so in practice a populated headers object is always returned.
   *
   * @param token - The Observe Inc ingest bearer token.
   * @returns A headers record suitable for OTLP HTTP exporters.
   */
  private buildAuthHeaders(token?: string): Record<string, string> {
    if (!token) {
      return {};
    }
    return { Authorization: `Bearer ${token}` };
  }

  /**
   * Creates and registers the {@link WebTracerProvider} with a
   * {@link BatchSpanProcessor} backed by the {@link OTLPTraceExporter}.
   *
   * The exporter targets `{observeCollectorUrl}/v1/traces` automatically
   * (the OTLP HTTP exporter appends the path suffix).
   *
   * `x-observe-target-package` is an Observe Inc routing header that directs
   * the payload to the correct internal ingest pipeline. Without it the
   * endpoint returns 415.
   *
   * @param collectorUrl - Base OTLP collector URL.
   * @param resource     - Service resource attributes.
   * @param headers      - Authentication headers.
   */
  private initTraceProvider(
    collectorUrl: string,
    resource: Resource,
    headers: Record<string, string>
  ): void {
    const exporter = new OTLPTraceExporter({
      url: `${collectorUrl}/v1/traces`,
      headers: { ...headers, 'x-observe-target-package': 'Tracing' }
    });

    // In SDK 2.x, span processors are supplied in the constructor config;
    // the addSpanProcessor() method was removed.
    this.tracerProvider = new WebTracerProvider({
      resource,
      spanProcessors: [new BatchSpanProcessor(exporter)]
    });

    // register() internally calls trace.setGlobalTracerProvider(this), making
    // the provider available everywhere via trace.getTracer() without needing a
    // direct reference to this service.
    this.tracerProvider.register();

    // Register the W3C Trace Context propagator so that the traceparent /
    // tracestate headers are injected on outbound XHRs (and read from inbound
    // responses) by XMLHttpRequestInstrumentation. Without this the default
    // no-op propagator is used, which means no traceparent header is added even
    // when propagateTraceHeaderCorsUrls matches the request URL.
    propagation.setGlobalPropagator(new W3CTraceContextPropagator());

    // Activate XHR auto-instrumentation after the global tracer provider is set.
    // registerInstrumentations picks up the provider registered above automatically
    // — passing a provider reference is intentionally omitted per the browser SDK
    // pattern.  Every XMLHttpRequest made by Angular's HttpClient (which uses XHR
    // internally) will automatically become a child span under the active OTel
    // context and have the W3C traceparent header injected on matching origins.
    registerInstrumentations({
      instrumentations: [
        new XMLHttpRequestInstrumentation({
          /**
           * Inject the W3C `traceparent` header on cross-origin requests to
           * ACAdvantage API endpoints so that server-side spans can be linked to
           * the browser root span in distributed traces.
           */
          propagateTraceHeaderCorsUrls: [/https:\/\/.*\.acacceptance\.com/],
          /**
           * Suppress spans for telemetry egress and third-party scripts to avoid
           * noisy, irrelevant child spans in Observe traces and to prevent
           * infinite-loop telemetry spans from the OTLP exporter's own XHRs.
           */
          ignoreUrls: [
            /collect\.observeinc\.com/,
            /onetrust\.com/,
            /cookielaw\.org/,
            /fullstory\.com/
          ]
        })
      ]
    });
  }

  /**
   * Creates and registers the {@link MeterProvider} with a
   * {@link PeriodicExportingMetricReader} backed by the
   * {@link OTLPMetricExporter}.
   *
   * Metrics are pushed every {@link METRIC_EXPORT_INTERVAL_MS} milliseconds.
   *
   * `x-observe-target-package` is an Observe Inc routing header that directs
   * the payload to the correct internal ingest pipeline. Without it the
   * endpoint returns 415.
   *
   * @param collectorUrl - Base OTLP collector URL.
   * @param resource     - Service resource attributes.
   * @param headers      - Authentication headers.
   */
  private initMeterProvider(
    collectorUrl: string,
    resource: Resource,
    headers: Record<string, string>
  ): void {
    const exporter = new OTLPMetricExporter({
      url: `${collectorUrl}/v1/metrics`,
      headers: { ...headers, 'x-observe-target-package': 'Metrics' }
    });

    this.meterProvider = new MeterProvider({
      resource,
      readers: [
        new PeriodicExportingMetricReader({
          exporter,
          exportIntervalMillis: METRIC_EXPORT_INTERVAL_MS
        })
      ]
    });

    // Register as the global meter provider so that metrics.getMeter() works
    // anywhere in the application without a direct reference to this service.
    // This mirrors how initTraceProvider() calls trace.setGlobalTracerProvider()
    // and ensures that subsequent TelemetryService instances (blocked by the
    // cross-instance sentinel) can still obtain a functional meter via the
    // global API rather than a silent no-op.
    metrics.setGlobalMeterProvider(this.meterProvider);
  }
}
